export const POINTER_TARGET_MIN_CONFIDENCE = 0.5
export const POINTER_TARGET_TTL_MS = 1500
export const POINTER_TARGET_REFRESH_MS = 750

function normalizeTimestamp(value, fallback) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback
  return parsed < 10000000000 ? Math.round(parsed * 1000) : Math.round(parsed)
}

function validTarget(target) {
  return Boolean(
    target
    && typeof target.objectId === 'string' && target.objectId
    && ['reservoir', 'region'].includes(target.objectType)
    && typeof target.displayName === 'string' && target.displayName
  )
}

export class GesturePointerBinding {
  constructor({
    assistant,
    getScene,
    minConfidence = POINTER_TARGET_MIN_CONFIDENCE,
    ttlMs = POINTER_TARGET_TTL_MS,
    refreshMs = POINTER_TARGET_REFRESH_MS,
    now = () => Date.now(),
    setTimer = (callback, delay) => setTimeout(callback, delay),
    clearTimer = timer => clearTimeout(timer),
  }) {
    this.assistant = assistant
    this.getScene = getScene
    this.minConfidence = minConfidence
    this.ttlMs = ttlMs
    this.refreshMs = refreshMs
    this.now = now
    this.setTimer = setTimer
    this.clearTimer = clearTimer
    this.lastTargetKey = ''
    this.lastReportedAt = 0
    this.lastSeenAt = 0
    this.lastScreen = { x: 0, y: 0 }
    this.hasPublished = false
    this.expiryTimer = null
  }

  handleFrame(frame = {}) {
    const now = this.now()
    const screenX = Number(frame.x)
    const screenY = Number(frame.y)
    const confidence = Number(frame.confidence)
    const timestamp = normalizeTimestamp(frame.timestamp, now)
    this.lastScreen = {
      x: Number.isFinite(screenX) ? screenX : 0,
      y: Number.isFinite(screenY) ? screenY : 0,
    }

    if (frame.controlEnabled === false || frame.pointerActive === false) {
      return this.clear('pointer_inactive', false, timestamp)
    }
    if (!Number.isFinite(screenX) || !Number.isFinite(screenY)) {
      return this.clear('screen_coordinate_invalid', false, timestamp)
    }
    if (!Number.isFinite(confidence) || confidence < this.minConfidence) {
      return this.clear('low_confidence', false, timestamp)
    }
    if (now - timestamp > this.ttlMs) {
      return this.clear('stale_frame', false, timestamp)
    }

    const scene = this.getScene && this.getScene()
    if (!scene || typeof scene.assistantPickPointerTarget !== 'function') {
      return this.clear('scene_not_ready', false, timestamp)
    }

    let target = validTarget(frame.resolvedTarget) ? frame.resolvedTarget : null
    if (!target) {
      try {
        target = scene.assistantPickPointerTarget(screenX, screenY)
      } catch (_) {
        target = null
      }
    }
    if (!validTarget(target)) return this.clear('no_business_target', false, timestamp)

    this.lastSeenAt = now
    this._armExpiry()
    const key = `${target.objectType}:${target.objectId}`
    if (this.lastTargetKey === key && now - this.lastReportedAt < this.refreshMs) {
      return { status: 'deduplicated', target }
    }

    this._publish({
      objectId: target.objectId,
      objectType: target.objectType,
      displayName: target.displayName,
      screenX,
      screenY,
      confidence,
      timestamp,
      ...(Array.isArray(target.candidates) ? { candidates: target.candidates } : {}),
    })
    this.lastTargetKey = key
    this.lastReportedAt = now
    return { status: 'reported', target }
  }

  clear(reason = 'cleared', force = false, timestamp = this.now()) {
    this._cancelExpiry()
    const shouldPublish = force || !this.hasPublished || Boolean(this.lastTargetKey)
    this.lastTargetKey = ''
    this.lastSeenAt = 0
    if (!shouldPublish) return { status: 'deduplicated-clear', reason }
    this._publish({
      objectId: '',
      objectType: '',
      displayName: '',
      screenX: this.lastScreen.x,
      screenY: this.lastScreen.y,
      confidence: 0,
      timestamp: normalizeTimestamp(timestamp, this.now()),
    })
    this.lastReportedAt = this.now()
    return { status: 'cleared', reason }
  }

  expireStale(at = this.now()) {
    if (!this.lastTargetKey || !this.lastSeenAt || at - this.lastSeenAt < this.ttlMs) return false
    this.clear('target_expired', true, at)
    return true
  }

  dispose() {
    this._cancelExpiry()
  }

  _publish(payload) {
    if (this.assistant && typeof this.assistant.updatePointerTarget === 'function') {
      this.assistant.updatePointerTarget(payload)
    }
    this.hasPublished = true
  }

  _armExpiry() {
    this._cancelExpiry()
    this.expiryTimer = this.setTimer(() => {
      this.expiryTimer = null
      this.expireStale(this.now())
    }, this.ttlMs)
  }

  _cancelExpiry() {
    if (this.expiryTimer !== null) this.clearTimer(this.expiryTimer)
    this.expiryTimer = null
  }
}
